import React, { useState, useEffect, useRef } from "react";
import * as echarts from "echarts";
import moment from "moment";
import Loader from "react-spinner-loader";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { 
  faSkull, 
  faHeartbeat, 
  faTent, 
 
  faHouseChimneyCrack,
  faTruckMedical,
 
  faInfoCircle,
  faCity,
  faHome,
  faUsers,
  faAmbulance,
  faCampground,
  faHouseFloodWater,
  faHouseCrack
} from "@fortawesome/free-solid-svg-icons";
import { EarhquakeIcon as SidebarEarthquakeIcon } from "../../../../Components/IconComponents";
import { getData, postDataWithBody } from "../../../../Services/Services";
import { 
  earthquakecardsdata, 
  earthquakechartdata, 
  earthquakepopupdata 
} from "../../../../Services/EndPoints";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./EarthquakeDashboard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// ── Earthquake Theme Palette ─────────────────────────────────────────────
// ── Blue-only palette (matching cold wave tokens) ────────────────────────
const C = {
  bg: "#F0F6FB",
  surface: "#FFFFFF",
  white: "#FFFFFF",
  navBg: "#1C4E72",
  primary: "#2A6591",
  text1: "#0F2D42",
  text: "#0F2D42",
  text2: "#3A5F78",
  text3: "#7A9BB0",
  muted: "#7A9BB0",
  border: "rgba(42,101,145,.10)",
};

const PALETTE = [
  { bg: "#FFF0F0", color: "#D95555" },
  { bg: "#FFF4EA", color: "#D4772A" },
  { bg: "#EEFAF3", color: "#27A05A" },
  { bg: "#EBF4FF", color: "#3579D4" },
];

// ── Card Configuration ─────────────────────────────────────────────────
const dashboardCardConfigForEarthquake = {
  title: "Earthquake Impact",
  data: [
    { key: "no_of_deaths", label: "Deaths", icon: faSkull, description: "Confirmed fatalities", secondaryIcon: faHouseCrack },
    { key: "no_of_injured_persons", label: "Injured", icon: faHeartbeat, description: "Requiring medical aid", secondaryIcon: faAmbulance },
    { key: "no_of_people_rescued", label: "Rescued", icon: faTruckMedical, description: "Safely evacuated", secondaryIcon: faUsers },
    { key: "no_of_houses_fully_damaged", label: "Fully Damaged", icon: faHouseFloodWater, description: "Complete structural loss", secondaryIcon: faHome },
    { key: "no_of_houses_partially_damaged", label: "Partially Damaged", icon: faHouseChimneyCrack, description: "Repairable structures", secondaryIcon: faHome },
    { key: "no_of_relief_camps", label: "Relief Camps", icon: faTent, description: "Active camp sites", secondaryIcon: faCampground },
  ],
};

const formatLabel = (key) => {
  const map = {
    no_of_deaths: "Deaths",
    no_of_injured_persons: "Injured",
    no_of_people_rescued: "Rescued",
    no_of_houses_fully_damaged: "Fully Damaged",
    no_of_houses_partially_damaged: "Partially Damaged",
    no_of_relief_camps: "Relief Camps",
  };
  return map[key] || key;
};

// ── SVG/FA Icon Wrapper (matching Avalanche for identical cards) ─────────
function EarthquakeIcon({ keyName = "", label = "", color = C.primary, faIcon }) {
  const k = (keyName + " " + label).toLowerCase();
  const sw = "1.55";
  const r = "round";

  // Use matching Avalanche icon for identical cards like "Deaths"
  if (k.includes("death") || k.includes("fatal") || k.includes("loss"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
        <circle cx="10" cy="6.5" r="2.8" stroke={color} strokeWidth={sw}/>
        <path d="M4.5 18c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" stroke={color} strokeWidth={sw} strokeLinecap={r}/>
        <path d="M7.8 5l4.4 4.4M12.2 5l-4.4 4.4" stroke={color} strokeWidth="1.4" strokeLinecap={r}/>
      </svg>
    );

  // Default to the specified FontAwesome icon for other cards
  return <FontAwesomeIcon icon={faIcon} style={{ fontSize: 18, color }} />;
}

// ── Bar Chart ───────────────────────────────────────────────────────────
const EarthquakeBarChart = ({ selectedIncident, startDateStr, endDateStr, onBarClick, isTrendExpanded }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const barColor = C.primary;

  const fetchChartData = async () => {
    if (!selectedIncident) return;
    setLoading(true);
    try {
      const response = await getData(
        earthquakechartdata.replace("FROMDATE", startDateStr).replace("TODATE", endDateStr).replace("BARTYPE", selectedIncident)
      );
      setChartData(response || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchChartData(); }, [selectedIncident, startDateStr, endDateStr]);

  useEffect(() => {
    if (!chartRef.current || !chartData.length) return;
    const sortedData = [...chartData.filter(i => i.count > 0)].sort((a, b) => a.count - b.count);

    const option = {
      title: {
        show: false,
      },
      tooltip: {
        trigger: "axis", axisPointer: { type: "shadow" },
        formatter: (p) => `<div style="font-weight:700;color:${C.text};border-bottom:2px solid ${barColor};margin-bottom:6px;padding-bottom:4px">${p[0].name}</div><span style="color:${C.muted}">Count: </span><span style="font-weight:700;color:${barColor}">${p[0].value.toLocaleString('en-IN')}</span>`,
        backgroundColor: C.white, borderColor: C.border, borderWidth: 1,
      },
      grid: { left: "15%", right: "8%", bottom: "10%", top: "20%", containLabel: false },
      xAxis: { type: "value", axisLabel: { color: C.muted, fontSize: 11 }, splitLine: { lineStyle: { color: C.border, type: "dotted" } }, axisTick: { show: false } },
      yAxis: { type: "category", data: sortedData.map(i => i.dist_name), axisLabel: { color: C.text, fontSize: 12, fontWeight: "600", padding: [0, 10, 0, 0] }, axisLine: { lineStyle: { color: C.border, width: 2 } } },
      series: [{
        type: "bar",
        data: sortedData.map(item => ({
          value: item.count,
          itemStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [{ offset: 0, color: "#A0C8E0" }, { offset: 1, color: C.primary }],
            },
            borderRadius: [0, 10, 10, 0],
          },
        })),
        barWidth: 15,
        barMaxWidth: 20,
        barCategoryGap: "20%",
        label: { show: true, position: "right", formatter: p => p.value.toLocaleString("en-IN"), fontSize: 11, fontWeight: "700", color: C.primary },
        showBackground: true, backgroundStyle: { color: "rgba(42,101,145,.04)", borderRadius: [0, 10, 10, 0] },
      }],
      dataZoom: sortedData.length > 6 ? [{ type: "slider", yAxisIndex: 0, start: 0, end: 60, orient: "vertical", handleStyle: { color: barColor, borderColor: barColor }, borderColor: C.border, backgroundColor: C.white }] : [],
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    chartInstance.current.on("click", (params) => {
      if (params.componentType === "series") {
        const clicked = chartData.find(d => d.dist_name === params.name);
        if (clicked && onBarClick) onBarClick(clicked);
      }
    });
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM already removed by React */ }
      chartInstance.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, selectedIncident, isTrendExpanded]);

  if (!selectedIncident) return null;

  if (loading) return (
    <div style={{ textAlign: "center", padding: 40 }}>
      <Loader show={true} type="body" stack="vertical" message="Analyzing seismic data..." />
    </div>
  );

  if (!chartData.length || !chartData.some(i => i.count > 0)) return (
    <div style={{ textAlign: "center", padding: 40, color: "var(--eqd-text-3)", fontSize: 13 }}>
      No data available for the selected period
    </div>
  );

  const validBars = chartData.filter(i => i.count > 0).length;
  const dynamicHeight = isTrendExpanded ? "calc(100vh - 160px)" : Math.max(150, Math.min(500, validBars * 45 + 80));

  return <div ref={chartRef} style={{ width: "100%", height: dynamicHeight }} />;
};

// ── Donut Chart ─────────────────────────────────────────────────────────
const DonutChart = ({ cardData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForEarthquake.data;
    const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0", "#7AB4D0", "#A0C8E0"];
    const seriesData = keys.map((cfg, idx) => ({
      name: formatLabel(cfg.key),
      value: cardData[cfg.key] || 0,
      itemStyle: {
        color: blueShades[idx % blueShades.length],
        borderRadius: 6,
        borderColor: C.white,
        borderWidth: 3,
      },
    })).filter(d => d.value > 0);

    const total = seriesData.reduce((s, d) => s + d.value, 0);

    const option = {
      title: {
        text: "IMPACT DISTRIBUTION",
        left: "center", top: 16,
        textStyle: { color: C.text, fontSize: 13, fontWeight: "700", fontFamily: "'Poppins', sans-serif" },
      },
      tooltip: {
        trigger: "item",
        formatter: (p) => `
          <div style="font-family:'Poppins',sans-serif;padding:4px 2px">
            <div style="font-weight:700;color:${C.text};margin-bottom:4px">${p.name}</div>
            <div style="color:${p.color};font-weight:800;font-size:18px">${p.value.toLocaleString('en-IN')}</div>
            <div style="color:${C.muted};font-size:11px">${p.percent.toFixed(1)}% of total</div>
          </div>`,
        backgroundColor: C.white, borderColor: C.border, borderWidth: 1,
        extraCssText: "box-shadow:0 4px 16px rgba(0,0,0,0.12);border-radius:12px;",
      },
      legend: {
        orient: "horizontal", bottom: 12,
        itemWidth: 10, itemHeight: 10,
        itemGap: 14,
        textStyle: { color: C.muted, fontSize: 10, fontWeight: "600" },
        formatter: (name) => name,
      },
      graphic: [{
        type: "text",
        left: "center", top: "43%",
        style: {
          text: total.toLocaleString('en-IN'),
          textAlign: "center",
          fill: C.text, fontSize: 22, fontWeight: "800", fontFamily: "'Poppins',sans-serif",
        },
      }, {
        type: "text",
        left: "center", top: "51%",
        style: {
          text: "TOTAL",
          textAlign: "center",
          fill: C.muted, fontSize: 10, fontWeight: "600", fontFamily: "'Poppins',sans-serif",
          letterSpacing: 2,
        },
      }],
      series: [{
        name: "Impact",
        type: "pie",
        radius: ["42%", "68%"],
        center: ["50%", "50%"],
        avoidLabelOverlap: true,
        padAngle: 2,
        label: { show: false },
        emphasis: {
          scale: true, scaleSize: 6,
          label: { show: false },
          itemStyle: { shadowBlur: 20, shadowColor: "rgba(0,0,0,0.2)" },
        },
        data: seriesData,
      }],
    };

    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", handleResize);
    return () => { window.removeEventListener("resize", handleResize); chartInstance.current?.dispose(); };
  }, [cardData]);

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 24, boxShadow: C.shadow, height: 380, overflow: "hidden" }}>
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

// ── Radial Bar Chart ────────────────────────────────────────────────────
const RadialChart = ({ cardData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForEarthquake.data;
    const total = keys.reduce((s, c) => s + (cardData[c.key] || 0), 0) || 1;
    const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0", "#7AB4D0", "#A0C8E0"];
    const seriesData = keys.map((cfg, idx) => ({
      name: formatLabel(cfg.key),
      value: cardData[cfg.key] || 0,
      pct: parseFloat(((cardData[cfg.key] || 0) / total * 100).toFixed(1)),
      color: blueShades[idx % blueShades.length],
    })).sort((a, b) => b.value - a.value);

    const option = {
      title: {
        text: "CATEGORY BREAKDOWN",
        left: "center", top: 16,
        textStyle: { color: C.text, fontSize: 13, fontWeight: "700", fontFamily: "'Poppins', sans-serif" },
      },
      tooltip: {
        trigger: "axis", axisPointer: { type: "none" },
        formatter: (params) => {
          const p = params[0];
          const item = seriesData.find(d => d.name === p.name);
          return `
            <div style="font-family:'Poppins',sans-serif;padding:4px 2px">
              <div style="font-weight:700;color:${C.text};margin-bottom:4px">${p.name}</div>
              <div style="color:${item?.color};font-weight:800;font-size:18px">${(item?.value || 0).toLocaleString('en-IN')}</div>
              <div style="color:${C.muted};font-size:11px">${item?.pct}% share</div>
            </div>`;
        },
        backgroundColor: C.white, borderColor: C.border, borderWidth: 1,
        extraCssText: "box-shadow:0 4px 16px rgba(0,0,0,0.12);border-radius:12px;",
      },
      grid: { left: 16, right: 60, top: 52, bottom: 16, containLabel: true },
      xAxis: {
        type: "value", show: false,
        max: (val) => val.max * 1.25,
      },
      yAxis: {
        type: "category",
        data: seriesData.map(d => d.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: C.text, fontSize: 11, fontWeight: "600",
          fontFamily: "'Poppins',sans-serif",
          margin: 12,
        },
      },
      series: [
        {
          type: "bar",
          data: seriesData.map(d => ({ value: Math.max(...seriesData.map(s => s.value)) * 1.2, itemStyle: { color: `${C.border}`, borderRadius: 30 } })),
          barWidth: 12,
          barGap: "-100%",
          silent: true,
          label: { show: false },
          emphasis: { disabled: true },
          zlevel: 0,
        },
        {
          type: "bar",
          data: seriesData.map(d => ({
            value: d.value,
            itemStyle: {
              color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0,
                colorStops: [{ offset: 0, color: d.color + "aa" }, { offset: 1, color: d.color }] },
              borderRadius: 30,
            },
          })),
          barWidth: 12,
          label: {
            show: true, position: "right",
            formatter: (p) => {
              const item = seriesData[p.dataIndex];
              return `{val|${p.value.toLocaleString('en-IN')}}  {pct|${item.pct}%}`;
            },
            rich: {
              val: { color: C.text, fontSize: 11, fontWeight: "700" },
              pct: { color: C.muted, fontSize: 10 },
            },
          },
          zlevel: 1,
        },
      ],
    };

    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", handleResize);
    return () => { window.removeEventListener("resize", handleResize); chartInstance.current?.dispose(); };
  }, [cardData]);

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 24, boxShadow: C.shadow, height: 380, overflow: "hidden" }}>
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

// ── Spider / Radar Chart ────────────────────────────────────────────────
const SpiderChart = ({ cardData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForEarthquake.data;
    const values = keys.map(c => cardData[c.key] || 0);
    const maxVal = Math.max(...values, 1);

    const shortLabel = {
      no_of_deaths: "Deaths",
      no_of_injured_persons: "Injured",
      no_of_people_rescued: "Rescued",
      no_of_houses_fully_damaged: "Full\nDamage",
      no_of_houses_partially_damaged: "Partial\nDamage",
      no_of_relief_camps: "Relief\nCamps",
    };

    const indicator = keys.map(cfg => ({
      name: shortLabel[cfg.key] || cfg.label,
      max: maxVal * 1.3,
    }));

    const option = {
      title: {
        text: "IMPACT RADAR",
        left: "center", top: 16,
        textStyle: { color: C.text, fontSize: 13, fontWeight: "700", fontFamily: "'Poppins', sans-serif" },
      },
      tooltip: {
        trigger: "item",
        formatter: (p) => {
          if (!p.data?.value) return "";
          const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0", "#7AB4D0", "#A0C8E0"];
          return keys.map((cfg, i) => `
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
              <div style="width:8px;height:8px;border-radius:50%;background:${blueShades[i % blueShades.length]}"></div>
              <span style="color:${C.muted};font-size:11px">${formatLabel(cfg.key)}:</span>
              <span style="font-weight:700;color:${C.text}">${(p.data.value[i] || 0).toLocaleString('en-IN')}</span>
            </div>`).join("");
        },
        backgroundColor: C.white, borderColor: C.border, borderWidth: 1,
        extraCssText: "box-shadow:0 4px 16px rgba(0,0,0,0.12);border-radius:12px;padding:12px 16px;",
      },
      radar: {
        indicator,
        center: ["50%", "54%"],
        radius: "58%",
        startAngle: 90,
        splitNumber: 4,
        axisName: {
          color: C.text,
          fontSize: 10,
          fontWeight: "700",
          fontFamily: "'Poppins', sans-serif",
          padding: [0, 4],
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: [
              "rgba(42,101,145,.04)", "rgba(42,101,145,.06)",
              "rgba(42,101,145,.08)", "rgba(42,101,145,.12)",
            ],
          },
        },
        splitLine: { lineStyle: { color: C.border, width: 1 } },
        axisLine: { lineStyle: { color: C.border, width: 1 } },
      },
      series: [{
        type: "radar",
        data: [{
          value: values,
          name: "Impact Levels",
          symbol: "circle",
          symbolSize: 7,
          lineStyle: { color: C.primary, width: 2.5, shadowBlur: 8, shadowColor: "rgba(42,101,145,.3)" },
          areaStyle: {
            color: {
              type: "radial", x: 0.5, y: 0.5, r: 1,
              colorStops: [
                { offset: 0, color: "rgba(42,101,145,.15)" },
                { offset: 1, color: "rgba(42,101,145,.05)" },
              ],
            },
          },
          itemStyle: {
            color: C.white,
            borderColor: C.primary,
            borderWidth: 2.5,
          },
          label: { show: false },
        }],
      }],
    };

    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", handleResize);
    return () => { window.removeEventListener("resize", handleResize); chartInstance.current?.dispose(); };
  }, [cardData]);

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 24, boxShadow: C.shadow, height: 380, overflow: "hidden" }}>
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

// ── District Popup ───────────────────────────────────────────────────────
const DistrictPopup = ({ show, onHide, districtData, incidentType, blocks }) => {
  const barColor = C.primary;
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
      onClick={onHide}
    >
      <div
        style={{
          background: C.white, borderRadius: 20,
          width: "100%", maxWidth: 480,
          boxShadow: "0 24px 60px -12px rgba(0,0,0,0.3)",
          border: `1px solid ${C.border}`, overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.navBg}, ${C.primary})`,
          padding: "14px 18px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: `3px solid ${barColor}`,
          gap: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{
              background: "rgba(255,255,255,0.2)", width: 36, height: 36, flexShrink: 0,
              borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            }}><SidebarEarthquakeIcon width="18" height="18" style={{ fill: 'white' }} /></div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.75)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Earthquake Impact Zone
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "white", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {districtData} • {formatLabel(incidentType)}
              </div>
            </div>
          </div>
          <button
            onClick={onHide}
            style={{
              background: "rgba(255,255,255,0.2)", border: "1.5px solid rgba(255,255,255,0.4)",
              color: "white", borderRadius: 8, width: 30, height: 30, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", fontSize: 15, fontWeight: 700, lineHeight: 1,
              transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.35)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: 16, background: C.bg, maxHeight: 360, overflowY: "auto" }}>
          {blocks?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {blocks.map((block, i) => (
                <div
                  key={i}
                  style={{
                    background: C.white, border: `1px solid ${C.border}`,
                    borderLeft: `4px solid ${barColor}`,
                    borderRadius: 12, padding: "10px 14px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    transition: "transform 0.15s ease", cursor: "default",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateX(3px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateX(0)"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, color: C.text, fontSize: 13 }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: 7,
                      background: `${barColor}15`, display: "flex", alignItems: "center",
                      justifyContent: "center", color: barColor, fontSize: 11, fontWeight: 800,
                    }}>{i + 1}</div>
                    <FontAwesomeIcon icon={faCity} style={{ color: barColor, fontSize: 12 }} />
                    <span>{block.block_name}</span>
                  </div>
                  <div style={{
                    background: barColor, color: "white", padding: "5px 14px",
                    borderRadius: 20, fontWeight: 700, fontSize: 14,
                    minWidth: 48, textAlign: "center",
                  }}>
                    {block.count.toLocaleString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: "center", padding: "32px 20px",
              background: C.white, borderRadius: 14, border: `2px dashed ${C.border}`,
            }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: 32, marginBottom: 10, color: C.muted }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>No Block Data Available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ───────────────────────────────────────────────────────
export default function EarthquakeDashboard() {
  const handle = useFullScreenHandle();
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTrendExpanded, setIsTrendExpanded] = useState(false);
  const [isTrendMinimized, setIsTrendMinimized] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [cardData, setCardData] = useState({});
  const [selectedIncident, setSelectedIncident] = useState("");
  const [popupState, setPopupState] = useState({ show: false, district: null, blocks: [], incidentType: null });

  const [internalFromDate, setInternalFromDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [internalToDate, setInternalToDate]   = useState(new Date());
  const [appliedFromDate, setAppliedFromDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [appliedToDate, setAppliedToDate]     = useState(new Date());
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchCardData = async (from = appliedFromDate, to = appliedToDate) => {
    setLoader(true); setError(null);
    const s = moment(from).format("YYYY-MM-DD");
    const e = moment(to).format("YYYY-MM-DD");
    try {
      const data = await getData(earthquakecardsdata.replace("FROMDATE", s).replace("TODATE", e));
      setCardData(data?.[0] || {});
    } catch (err) { setError("Failed to load data. Please try again."); }
    finally { setLoader(false); }
  };

  const handleApply = () => {
    if (internalFromDate > internalToDate) { setError("From date cannot be greater than To date"); return; }
    setAppliedFromDate(internalFromDate);
    setAppliedToDate(internalToDate);
    fetchCardData(internalFromDate, internalToDate);
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    const today = new Date();
    const fromDate = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);

    // If selected year is future, clamp fromDate to today
    const clampedFrom = fromDate > today ? today : fromDate;
    // toDate is either today (if current/past year within range) or year end, but never beyond today
    const clampedTo = yearEnd > today ? today : yearEnd;

    setInternalFromDate(clampedFrom);
    setInternalToDate(clampedTo);
    setAppliedFromDate(clampedFrom);
    setAppliedToDate(clampedTo);
    fetchCardData(clampedFrom, clampedTo);
  };

  const handleBarClick = async (district) => {
    try {
      const regionIds = district.region_id.split(",").map(Number);
      const blockData = await postDataWithBody(earthquakepopupdata, { region_id_list: regionIds, from_date: appliedFromDate, to_date: appliedToDate, bar_type: selectedIncident }, {});
      setPopupState({ show: true, district: district.dist_name, blocks: blockData || [], incidentType: selectedIncident });
    } catch {
      setPopupState({ show: true, district: district.dist_name, blocks: [], incidentType: selectedIncident });
    }
  };

  useEffect(() => {
    if (isInitialLoad) { fetchCardData(); setIsInitialLoad(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoad]);

  useEffect(() => {
    setError(internalFromDate > internalToDate ? "Select proper From Date and To Date" : null);
  }, [internalFromDate, internalToDate]);

  const startDateStr = moment(appliedFromDate).format("YYYY-MM-DD");
  const endDateStr = moment(appliedToDate).format("YYYY-MM-DD");

  const toggleFullscreen = () => {
    isFullscreen ? handle.exit() : handle.enter();
    setIsFullscreen(!isFullscreen);
  };
  const ReadOnlyDateInput = React.forwardRef((props, ref) => (
  <input {...props} ref={ref} onKeyDown={e => e.preventDefault()} readOnly />
));

  return (
    <FullScreen handle={handle}>
    <div className={`eqd-root${isFullscreen ? " eqd-fs" : ""}`}>
      {/* ── Navbar ── */}
      <div className="eqd-nav">
        <div className="eqd-nav-brand">
          <div className="eqd-nav-logo">
            <SidebarEarthquakeIcon width="18" height="18" style={{ fill: 'white' }} />
          </div>
          <div className="eqd-nav-title" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "1rem", fontWeight: 800, letterSpacing: "0.5px" }}>Earthquake</span>
            <span style={{ fontSize: "1rem", fontWeight: 800, letterSpacing: "0.5px" }}>Dashboard</span>
          </div>
        </div>
        
        <div className="eqd-nav-right">
          <div className="eqd-nav-group">
            <span className="eqd-nav-label">Year</span>
            <select className="eqd-nav-select" value={selectedYear} onChange={handleYearChange}>
              <option value={2024}>2024</option>
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </select>
          </div>

          <div className="eqd-daterange">
            <div className="eqd-daterange-field">
              <span className="eqd-dr-label">FROM</span>
              <ReactDatePicker
                selected={internalFromDate}
                onChange={date => setInternalFromDate(date)}
                dateFormat="dd/MM/yyyy"
                isClearable placeholderText="Select date" todayButton="Today"
                className="eqd-dp-input" clearButtonClassName="eqd-dp-clear"
                customInput={<ReadOnlyDateInput />}
              />
            </div>
            <div className="eqd-dr-divider" />
            <div className="eqd-daterange-field">
              <span className="eqd-dr-label">TO</span>
              <ReactDatePicker
                selected={internalToDate}
                onChange={date => setInternalToDate(date)}
                dateFormat="dd/MM/yyyy"
                isClearable placeholderText="Select date" todayButton="Today"
                className="eqd-dp-input" clearButtonClassName="eqd-dp-clear"
                customInput={<ReadOnlyDateInput />}
              />
            </div>
          </div>
          
          <button className="eqd-btn-apply" onClick={handleApply}>
            Apply
          </button>

          <button className="eqd-btn-icon" onClick={toggleFullscreen}>
            {isFullscreen ? (
              <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                <path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                <path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="eqd-error-strip">
          <FontAwesomeIcon icon={faInfoCircle} /> {error}
        </div>
      )}

      {/* ── Main Content ── */}
      <div className="eqd-content">
        <Loader show={loader} type="body" stack="vertical" message="Analyzing seismic data..." />
        
        {/* ── STAT CARDS ── */}
        <div className="eqd-cards-row">
          <div className="eqd-cards-inner">
            {dashboardCardConfigForEarthquake.data.map((cfg, idx) => {
              const p = PALETTE[idx % PALETTE.length];
              const active = selectedIncident === cfg.key;
              return (
                <div 
                  key={cfg.key}
                  className={`eqd-card-item${active ? " eqd-card-item--active" : ""}`}
                  onClick={() => setSelectedIncident(cfg.key)} 
                >
                  <span className="eqd-card-icon" style={{ background: p.bg }}>
                    <EarthquakeIcon keyName={cfg.key} label={cfg.label} color={p.color} faIcon={cfg.icon} />
                  </span>
                  <span className="eqd-card-text">
                    <span className="eqd-card-label">{cfg.label}</span>
                    <span className="eqd-card-value">
                      {cardData[cfg.key] != null ? cardData[cfg.key].toLocaleString("en-IN") : "0"}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── BAR CHART AREA ── */}
        <div className={`eqd-chart-wrap${isTrendExpanded ? " eqd-chart-wrap--expanded" : ""}`}>
          <div className="eqd-chart-top">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div>
                <h3 className="eqd-chart-heading">
                  {selectedIncident ? formatLabel(selectedIncident) : "Incident Trend Overview"}
                </h3>
                {!selectedIncident && (
                  <p className="eqd-chart-range">Select an impact category from the cards above to view its trend</p>
                )}
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {selectedIncident && (
                <button className="eqd-badge-btn" onClick={() => setSelectedIncident("")}>
                  {formatLabel(selectedIncident)} ✕
                </button>
              )}
              
              {!isTrendExpanded && (
                <button 
                  className="eqd-btn-icon" 
                  style={{ background: "rgba(2,132,199,.08)", border: "1px solid rgba(2,132,199,.12)", color: C.primary }}
                  onClick={() => setIsTrendMinimized(!isTrendMinimized)}
                  title={isTrendMinimized ? "Expand Card" : "Collapse Card"}
                >
                  {isTrendMinimized ? (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8M8 4v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                  ) : (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                  )}
                </button>
              )}

              <button 
                className="eqd-btn-icon" 
                style={{ background: "rgba(2,132,199,.08)", border: "1px solid rgba(2,132,199,.12)", color: C.primary }}
                onClick={() => setIsTrendExpanded(!isTrendExpanded)}
                title={isTrendExpanded ? "Collapse Fullscreen" : "Expand Fullscreen"}
              >
                {isTrendExpanded ? (
                  <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M13 7H9V3M9 7L15 1M7 13V9H3M7 9L1 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : (
                  <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M10 1H15V6M15 1L9 7M6 15H1V10M1 15L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </button>
            </div>
          </div>

          {(isTrendExpanded || !isTrendMinimized) && (
            <>
            {!selectedIncident && (
              <div className="eqd-chart-empty">
                <div className="eqd-chart-empty-icon">
                  <svg viewBox="0 0 48 48" fill="none" width="40" height="40">
                    <rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0"/>
                    <rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0"/>
                    <rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0"/>
                    <rect x="40" y="22" width="8" height="22" rx="2" fill="#5AA0C0"/>
                    <path d="M4 8h40" stroke="#C5DFF0" strokeWidth="1.5" strokeDasharray="3 3"/>
                  </svg>
                </div>
                <p className="eqd-chart-empty-text">Click any incident card above to visualize its trend</p>
              </div>
            )}
            <div className="eqd-chart-area">
              <EarthquakeBarChart 
                selectedIncident={selectedIncident} 
                startDateStr={startDateStr}
                endDateStr={endDateStr} 
                onBarClick={handleBarClick} 
                isTrendExpanded={isTrendExpanded}
              />
            </div>
            </>
          )}
        </div>

        {/* ── BOTTOM 3 CHARTS ── */}
        <div className="eqd-bottom-charts">
          <div className="eqd-chart-cell">
            <DonutChart cardData={cardData} />
          </div>
          <div className="eqd-chart-cell">
            <RadialChart cardData={cardData} />
          </div>
          <div className="eqd-chart-cell">
            <SpiderChart cardData={cardData} />
          </div>
        </div>

        {/* ── Popup ── */}
        <DistrictPopup 
          show={popupState.show} 
          onHide={() => setPopupState({ show: false, district: null, blocks: [], incidentType: null })}
          districtData={popupState.district} 
          incidentType={popupState.incidentType} 
          blocks={popupState.blocks} 
        />
      </div>
    </div>
    </FullScreen>
  );
}